/**
 * HeroBackdrop — motifs de fond très subtils pour les sections Hero.
 *
 * Famille visuelle commune sur tout le site : tracés fins, opacité très basse,
 * couleur dérivée du bleu institutionnel `#0D1B2A`. Aucun cliché tech.
 *
 * Chaque variant exprime une nuance différente (flux, croisements, modules,
 * réseaux, points, silence) tout en partageant le même vocabulaire graphique.
 *
 * Tous les motifs sont rendus en SVG inline (perf), avec `preserveAspectRatio
 * = xMidYMid slice` pour couvrir le conteneur sans déformation, et marqués
 * `aria-hidden` (décoratif).
 *
 * Couleurs :
 *   - tone "light" → trait #0D1B2A à très faible opacité (4–6 %)
 *   - tone "dark"  → trait #FDFDFC à très faible opacité (8–10 %)
 *
 * Usage :
 *   <HeroBackdrop variant="flows" />
 *   <HeroBackdrop variant="weave" tone="light" />
 */

const STROKE_LIGHT = "rgba(13, 27, 42, 0.07)";
const STROKE_LIGHT_SOFT = "rgba(13, 27, 42, 0.05)";
const FILL_LIGHT = "rgba(13, 27, 42, 0.08)";
const STROKE_DARK = "rgba(253, 253, 252, 0.1)";
const FILL_DARK = "rgba(253, 253, 252, 0.12)";

function Flows({ stroke }) {
    // Courbes douces évoquant la circulation de l'information
    return (
        <svg
            viewBox="0 0 1600 700"
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
        >
            <g
                fill="none"
                stroke={stroke}
                strokeWidth="0.8"
                strokeLinecap="round"
            >
                <path d="M-40 120 C 280 60, 540 220, 840 160 S 1380 80, 1660 200" />
                <path d="M-40 220 C 260 180, 560 320, 880 260 S 1380 200, 1660 320" />
                <path d="M-40 360 C 300 280, 600 460, 920 380 S 1380 320, 1660 460" />
                <path d="M-40 480 C 280 420, 560 580, 880 520 S 1380 460, 1660 600" />
                <path d="M-40 600 C 320 540, 620 700, 940 640 S 1380 600, 1660 720" />
            </g>
        </svg>
    );
}

function Weave({ stroke }) {
    // Trames diagonales : deux séries de lignes croisées, presque imperceptibles
    return (
        <svg
            viewBox="0 0 1600 700"
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
        >
            <defs>
                <pattern
                    id="weave-pat"
                    x="0"
                    y="0"
                    width="80"
                    height="80"
                    patternUnits="userSpaceOnUse"
                >
                    <line
                        x1="0"
                        y1="0"
                        x2="80"
                        y2="80"
                        stroke={stroke}
                        strokeWidth="0.6"
                    />
                    <line
                        x1="80"
                        y1="0"
                        x2="0"
                        y2="80"
                        stroke={stroke}
                        strokeWidth="0.6"
                    />
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#weave-pat)" />
        </svg>
    );
}

function Modules({ stroke }) {
    // Petits rectangles en grille lâche, irrégulière — structures modulaires
    return (
        <svg
            viewBox="0 0 1600 700"
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
        >
            <g fill="none" stroke={stroke} strokeWidth="0.8">
                {[
                    [80, 60, 110, 70],
                    [260, 120, 90, 60],
                    [430, 80, 130, 80],
                    [640, 140, 70, 50],
                    [780, 70, 110, 70],
                    [950, 130, 90, 60],
                    [1120, 90, 130, 80],
                    [1330, 150, 70, 50],
                    [40, 280, 130, 80],
                    [240, 320, 90, 60],
                    [400, 260, 110, 70],
                    [580, 320, 130, 80],
                    [780, 280, 90, 60],
                    [930, 320, 110, 70],
                    [1130, 260, 130, 80],
                    [1340, 320, 90, 60],
                    [100, 480, 110, 70],
                    [290, 520, 130, 80],
                    [490, 460, 90, 60],
                    [650, 520, 110, 70],
                    [840, 480, 130, 80],
                    [1050, 520, 90, 60],
                    [1210, 460, 110, 70],
                    [1380, 520, 130, 80],
                ].map(([x, y, w, h]) => (
                    <rect
                        key={`${x}-${y}`}
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                    />
                ))}
            </g>
        </svg>
    );
}

function Network({ stroke, fill }) {
    // Réseau : nœuds (points) reliés par des lignes fines
    const nodes = [
        [120, 90],
        [280, 200],
        [410, 80],
        [560, 180],
        [690, 70],
        [820, 220],
        [960, 110],
        [1080, 240],
        [1230, 90],
        [1380, 200],
        [180, 340],
        [340, 430],
        [490, 360],
        [620, 470],
        [780, 380],
        [930, 460],
        [1080, 380],
        [1240, 470],
        [1380, 360],
        [110, 560],
        [270, 620],
        [430, 540],
        [590, 630],
        [740, 560],
        [900, 620],
        [1050, 540],
        [1200, 630],
        [1370, 560],
    ];
    const edges = [
        [0, 1],
        [1, 2],
        [2, 3],
        [3, 4],
        [4, 5],
        [5, 6],
        [6, 7],
        [7, 8],
        [8, 9],
        [0, 10],
        [1, 11],
        [3, 12],
        [5, 14],
        [7, 16],
        [9, 18],
        [10, 11],
        [11, 12],
        [12, 13],
        [13, 14],
        [14, 15],
        [15, 16],
        [16, 17],
        [17, 18],
        [10, 19],
        [12, 21],
        [14, 23],
        [16, 25],
        [18, 27],
        [19, 20],
        [20, 21],
        [21, 22],
        [22, 23],
        [23, 24],
        [24, 25],
        [25, 26],
        [26, 27],
    ];
    return (
        <svg
            viewBox="0 0 1600 700"
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
        >
            <g stroke={stroke} strokeWidth="0.6" fill="none">
                {edges.map(([a, b], i) => (
                    <line
                        key={i}
                        x1={nodes[a][0]}
                        y1={nodes[a][1]}
                        x2={nodes[b][0]}
                        y2={nodes[b][1]}
                    />
                ))}
            </g>
            <g fill={fill}>
                {nodes.map(([x, y], i) => (
                    <circle key={i} cx={x} cy={y} r="1.6" />
                ))}
            </g>
        </svg>
    );
}

function Dots({ fill }) {
    // Grille très sparse de points — pour les pages calmes
    return (
        <svg
            viewBox="0 0 1600 700"
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
        >
            <defs>
                <pattern
                    id="dot-pat"
                    x="0"
                    y="0"
                    width="56"
                    height="56"
                    patternUnits="userSpaceOnUse"
                >
                    <circle cx="2" cy="2" r="0.9" fill={fill} />
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dot-pat)" />
        </svg>
    );
}

function Whisper({ fill }) {
    // Variante quasi-nulle : grille espacée, opacité encore plus basse
    // Pour les pages de lecture (FAQ) — ne distrait jamais
    return (
        <svg
            viewBox="0 0 1600 700"
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 h-full w-full opacity-60"
            aria-hidden="true"
        >
            <defs>
                <pattern
                    id="whisper-pat"
                    x="0"
                    y="0"
                    width="80"
                    height="80"
                    patternUnits="userSpaceOnUse"
                >
                    <circle cx="2" cy="2" r="0.7" fill={fill} />
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#whisper-pat)" />
        </svg>
    );
}

function ParallelLines({ stroke }) {
    // Lignes parallèles très fines, légèrement irrégulières
    // Pour Contact, Livre blanc — sobriété maximale
    return (
        <svg
            viewBox="0 0 1600 700"
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
        >
            <g stroke={stroke} strokeWidth="0.7" fill="none">
                <line x1="0" y1="80" x2="1600" y2="92" />
                <line x1="0" y1="180" x2="1600" y2="170" />
                <line x1="0" y1="280" x2="1600" y2="294" />
                <line x1="0" y1="380" x2="1600" y2="368" />
                <line x1="0" y1="480" x2="1600" y2="494" />
                <line x1="0" y1="580" x2="1600" y2="572" />
            </g>
        </svg>
    );
}

const VARIANTS = {
    flows: Flows,
    weave: Weave,
    modules: Modules,
    network: Network,
    dots: Dots,
    whisper: Whisper,
    lines: ParallelLines,
};

export default function HeroBackdrop({ variant = "flows", tone = "light" }) {
    const Pattern = VARIANTS[variant] || Flows;
    const isDark = tone === "dark";
    return (
        <div
            data-testid={`hero-backdrop-${variant}`}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 overflow-hidden"
        >
            <Pattern
                stroke={isDark ? STROKE_DARK : STROKE_LIGHT}
                strokeSoft={isDark ? STROKE_DARK : STROKE_LIGHT_SOFT}
                fill={isDark ? FILL_DARK : FILL_LIGHT}
            />
        </div>
    );
}
